import React, { Fragment } from 'react';
import { Button } from 'reactstrap';
import PropTypes from 'prop-types';
import { ImageMosaic, ImageMosaicDto, MosaicWall } from '@ami/react/core';
import CreateQuoteStep1Branch from './CreateQuoteStep1Branch';
import CreateQuoteStep1PriceZone from './CreateQuoteStep1PriceZone';
import CreateQuoteStep1SelectByComponent from './CreateQuoteStep1SelectByComponent';
import { CreateQuoteStep1 } from '../otherQuoteEntities';
import { ProductConfiguration } from '../../products/productConfiguration';
import { Machine } from '../../machines/machine';
import { PriceZoneSummary } from '../../price-zones/priceZone';
import { Spinner } from '@ami/react/core';
import { makeConcealable } from '@ami/react/core';
import { appConfig as config } from '@ami/react/core';
import { PageLoadingSpinner } from '@ami/react/core';

const CarrierSelection = makeConcealable((props) => (
  <div className="row my-4">
    <MosaicWall title="Select Carrier">
      {props.carrierDtos.map((x) => (
        <ImageMosaic
          key={x.id}
          data={x}
          mdWidthColumns={2}
          onClick={(carrierId) => props.onUpdate({ carrierId })}
        />
      ))}
    </MosaicWall>
  </div>
));

const ContinueButton = makeConcealable(({ onClick, submitting }) => (
  <div className="row justify-content-center pt-2 pb-5 mb-5">
    <Button
      size="lg"
      color="success"
      onClick={onClick}
      className="mt-3 mb-5"
      disabled={submitting}
    >
      Continue <Spinner show={submitting} />
    </Button>
  </div>
));

export default class CreateQuoteStep1Component extends React.PureComponent {
  static propTypes = {
    data: PropTypes.instanceOf(CreateQuoteStep1).isRequired,
    submitting: PropTypes.bool.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
    productConfiguration: PropTypes.instanceOf(ProductConfiguration).isRequired,
    machines: PropTypes.arrayOf(PropTypes.instanceOf(Machine)).isRequired,
    onCarrierSelected: PropTypes.func.isRequired,
    loadingMachines: PropTypes.bool.isRequired,
    isAmiPriceLookupMode: PropTypes.bool.isRequired,
    notAuthorized: PropTypes.bool,
    priceZones: PropTypes.arrayOf(PropTypes.instanceOf(PriceZoneSummary))
      .isRequired,
  };

  getCarrierDtos = () => {
    return this.props.productConfiguration.carriers.map(
      (x) =>
        new ImageMosaicDto({
          id: x.id,
          label: x.name,
          imageUrl: x.imageUrl,
          selected: x.id === this.props.data.carrierId,
          padding: 'p-3',
        })
    );
  };

  render() {
    const {
      data,
      machines,
      productConfiguration,
      onUpdate,
      onContinue,
      submitting,
      loadingMachines,
      isAmiPriceLookupMode,
      notAuthorized,
      priceZones,
    } = this.props;

    const pageTitle = isAmiPriceLookupMode ? 'Select Price Zone' : 'New Quote';

    if (notAuthorized) {
      return (
        <div className="container pt-2">
          <div className="hr-divider">
            <h3 className="hr-divider-content hr-divider-heading">
              {pageTitle}
            </h3>
          </div>
          <div className="row">
            <div className="col text-center">
              <strong>
                You do not have permission to create a quote for any branches
              </strong>
            </div>
          </div>
        </div>
      );
    }

    const selectedCarrier = productConfiguration.carriers.find(
      (x) => x.id === data.carrierId
    );

    return (
      <Fragment>
        {submitting && <PageLoadingSpinner />}

        {!submitting && (
          <div className="container pt-2">
            <div className="hr-divider">
              <h3 className="hr-divider-content hr-divider-heading">{pageTitle}</h3>
            </div>
            {!isAmiPriceLookupMode && (
              <CreateQuoteStep1Branch
                data={data}
                onUpdate={onUpdate}
                priceZones={priceZones}
              />
            )}
            {isAmiPriceLookupMode && (
              <CreateQuoteStep1PriceZone
                data={data}
                onUpdate={onUpdate}
                priceZones={priceZones}
              />
            )}
            <CarrierSelection
              show={!!data.dealerBranchId || !!data.priceZoneId}
              scrollOnShow={true}
              carrierDtos={this.getCarrierDtos()}
              onUpdate={(updateObj) => {
                var newCarrier = productConfiguration.carriers.find(
                  (x) => x.id === updateObj.carrierId
                );
                if (newCarrier.weightCategories.length === 1) {
                  updateObj.weightCategoryId = newCarrier.weightCategories[0].id;
                }
                onUpdate(updateObj);
              }}
            />
            <CreateQuoteStep1SelectByComponent
              data={data}
              onUpdate={onUpdate}
              setContact={this.props.setContact}
              productConfiguration={productConfiguration}
              machines={machines}
              selectedDealerBranch={data.dealerBranchSummary}
              show={
                (!!data.dealerBranchId || !!data.priceZoneId) && !!selectedCarrier
              }
              scrollOnShow={true}
              canSelect={
                selectedCarrier &&
                (config.isManagementApp || !selectedCarrier.isDisabled)
              }
              onSkipMakeAndModel={(weightCategory) =>
                onContinue(true, weightCategory)
              }
              loading={loadingMachines}
              isAmiPriceLookupMode={isAmiPriceLookupMode}
            />
            <ContinueButton
              show={
                !!(data.type && selectedCarrier && (config.isManagementApp || !selectedCarrier.isDisabled) && !loadingMachines)
              }
              scrollOnShow={true}
              onClick={() => onContinue(false)}
              submitting={submitting}
            />
          </div>
        )}
      </Fragment>
    );
  }
}
